import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchAttemptAudio } from '../services/lessons'

/**
 * One player for the archive: which row owns it, where it is, and whether
 * it is fetching. Recordings are bearer-gated (B1), so each one is pulled
 * through the authenticated client once, kept as an object URL for the
 * page's lifetime, and revoked on unmount.
 *
 * `error` is `'missing'` when the row has no recording to play and
 * `'failed'` when the fetch or the element failed; the page turns those
 * into sentences and clears them.
 */
export function useArchivePlayer() {
  const audioRef = useRef(null)
  const urlsRef = useRef(new Map())
  const [playingId, setPlayingId] = useState(null)
  const [loadingId, setLoadingId] = useState(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState(null)

  const element = useCallback(() => {
    if (audioRef.current) return audioRef.current
    const audio = new Audio()
    audio.preload = 'auto'
    audio.addEventListener('play', () => setIsPlaying(true))
    audio.addEventListener('pause', () => setIsPlaying(false))
    audio.addEventListener('ended', () => {
      setIsPlaying(false)
      setProgress(1)
    })
    audio.addEventListener('timeupdate', () => {
      if (audio.duration > 0) setProgress(Math.min(1, audio.currentTime / audio.duration))
    })
    audio.addEventListener('error', () => {
      setIsPlaying(false)
      setPlayingId(null)
      setError('failed')
    })
    audioRef.current = audio
    return audio
  }, [])

  useEffect(() => {
    const urls = urlsRef.current
    return () => {
      audioRef.current?.pause()
      urls.forEach((url) => URL.revokeObjectURL(url))
      urls.clear()
    }
  }, [])

  const toggle = useCallback(
    async (item) => {
      const audio = element()
      if (playingId === item.id) {
        if (audio.paused) await audio.play().catch(() => setError('failed'))
        else audio.pause()
        return
      }
      if (!item.userAudioUrl) {
        setError('missing')
        return
      }
      if (loadingId) return

      setLoadingId(item.id)
      setError(null)
      try {
        let url = urlsRef.current.get(item.id)
        if (!url) {
          url = await fetchAttemptAudio(item.userAudioUrl)
          urlsRef.current.set(item.id, url)
        }
        audio.pause()
        audio.src = url
        setProgress(0)
        setPlayingId(item.id)
        await audio.play()
      } catch {
        setPlayingId(null)
        setError('failed')
      } finally {
        setLoadingId(null)
      }
    },
    [element, loadingId, playingId],
  )

  const clearError = useCallback(() => setError(null), [])

  return { playingId, loadingId, isPlaying, progress, error, toggle, clearError }
}
